import {
  QaConcreteRepRef,
  QaLabel,
  QaLabelScope,
  QaLabelScopeEntry,
  QaLabelType,
  QaResolvedLabelSummary,
} from './types';

export interface QaLabelDefinition {
  description: string;
  allowsTm: boolean;
  shortcut: string;
  title: string;
}

export const QA_LABEL_TM_MIN = 60;
export const QA_LABEL_TM_MAX = 96;

const qaAllowedScopesByLabelType: Record<QaLabelType, QaLabelScope[]> = {
  [QaLabelType.NO_PRODUCT]: [QaLabelScope.TECHREP, QaLabelScope.TRIPLICATE, QaLabelScope.SAMPLE, QaLabelScope.ASSAY],
  [QaLabelType.PRODUCT]: [QaLabelScope.TECHREP, QaLabelScope.TRIPLICATE],
  [QaLabelType.PRIMER_DIMER]: [QaLabelScope.TECHREP, QaLabelScope.TRIPLICATE, QaLabelScope.SAMPLE, QaLabelScope.ASSAY],
  [QaLabelType.UNSPECIFIC]: [QaLabelScope.TECHREP, QaLabelScope.TRIPLICATE, QaLabelScope.SAMPLE, QaLabelScope.ASSAY],
  [QaLabelType.NONSENSE]: [QaLabelScope.TECHREP, QaLabelScope.TRIPLICATE, QaLabelScope.SAMPLE, QaLabelScope.ASSAY],
  [QaLabelType.ISSUE]: [
    QaLabelScope.CHIP,
    QaLabelScope.TECHREP,
    QaLabelScope.TRIPLICATE,
    QaLabelScope.SAMPLE,
    QaLabelScope.ASSAY,
  ],
  [QaLabelType.OTHER]: [
    QaLabelScope.CHIP,
    QaLabelScope.TECHREP,
    QaLabelScope.TRIPLICATE,
    QaLabelScope.SAMPLE,
    QaLabelScope.ASSAY,
  ],
};

export const qaLabelTypeOrder: QaLabelType[] = [
  QaLabelType.NO_PRODUCT,
  QaLabelType.PRODUCT,
  QaLabelType.PRIMER_DIMER,
  QaLabelType.UNSPECIFIC,
  QaLabelType.NONSENSE,
  QaLabelType.ISSUE,
  QaLabelType.OTHER,
];

export const qaLabelDefinitions: Record<QaLabelType, QaLabelDefinition> = {
  [QaLabelType.NO_PRODUCT]: {
    title: 'No Product',
    description:
      'Target product is biologically absent (or below LOQ). Use together with other labels if a Cq is present and lower than 28. Should be classified as Not Detected by future algorithms.',
    allowsTm: false,
    shortcut: '0',
  },
  [QaLabelType.PRODUCT]: {
    title: 'Product',
    description:
      'Target product is present. If a Cq is detected and no Tm is provided, the detected Cq and Tm are deemed correct. Should be detected and quantified by future algorithms.',
    allowsTm: true,
    shortcut: '1',
  },
  [QaLabelType.PRIMER_DIMER]: {
    title: 'Primer Dimer',
    description:
      'The pattern behaves like the NTC / known primer-dimer region. If not known, but only a suspected new primer dimer, mention this in the note. In the absence of a yes/no product label, should be marked as Unspecific by future algorithms.',
    allowsTm: true,
    shortcut: '2',
  },
  [QaLabelType.UNSPECIFIC]: {
    title: 'Unspecific',
    description:
      "There is a clear non-product peak that can still be pointed to, usually at the wrong Tm or as an extra peak in the same curve or triplicate. We don't know which peak is target product. Primer dimers are not suspected. In the absence of a yes/no product label, should be marked as Unspecific by future algorithms.",
    allowsTm: true,
    shortcut: '3',
  },
  [QaLabelType.NONSENSE]: {
    title: 'Nonsense',
    description:
      'Signal quality is too poor to point to one confident non-product Tm, or the curves across reps are inconsistent and all over the place, for example because of low fluorescence, broad peak shape(s), or noisy/volcano-like morphology. In the absence of a yes/no product label, should be marked as Unspecific by future algorithms.',
    allowsTm: false,
    shortcut: '4',
  },
  [QaLabelType.ISSUE]: {
    title: 'Issue',
    description:
      'An operational issue on a chip, assay, sample, triplicate, or individual well/replicate, for example a corner-well, pipetting, reagent, or machine problem that warrants re-analysis. Issue labels cannot be applied to results. Add a note to specify further, and use consistent keywords for similar cases.',
    allowsTm: false,
    shortcut: '8',
  },
  [QaLabelType.OTHER]: {
    title: 'Other',
    description:
      'Residual category for review-worthy curves that do not fit the other categories. Other labels cannot be applied to results. Add a note to specify further, and use consistent keywords for similar cases.',
    allowsTm: false,
    shortcut: '9',
  },
};

export type QaResultLabelKind = 'NONE' | 'PRODUCT' | 'NON_PRODUCT' | 'UNCLEAR' | 'ISSUE';

export function getQaAllowedScopesForLabelType(labelType: QaLabelType): QaLabelScope[] {
  return qaAllowedScopesByLabelType[labelType];
}

export function isQaLabelScopeAllowedForType(labelType: QaLabelType, scope: QaLabelScope): boolean {
  return qaAllowedScopesByLabelType[labelType].includes(scope);
}

export function getQaInvalidScopeMessage(labelType: QaLabelType, scope: QaLabelScope): string {
  if (scope === QaLabelScope.CHIP) {
    return 'CHIP scope is only allowed for ISSUE and OTHER';
  }

  return `${labelType} is not allowed at ${scope} scope`;
}

export function qaLabelTypeAllowsTm(labelType: QaLabelType): boolean {
  return qaLabelDefinitions[labelType].allowsTm;
}

export function doesQaLabelTypeAllowResultApplication(labelType: QaLabelType): boolean {
  return labelType !== QaLabelType.ISSUE && labelType !== QaLabelType.OTHER;
}

export function getQaInvalidResultApplicationMessage(labelType: QaLabelType): string {
  return `${labelType} cannot be set to affect results`;
}

export function isQaLabelTmInRange(tm: number): boolean {
  return Number.isInteger(tm) && tm >= QA_LABEL_TM_MIN && tm <= QA_LABEL_TM_MAX;
}

export function getQaLabelScopeSpecificity(scope: QaLabelScope): number {
  switch (scope) {
    case QaLabelScope.CHIP:
      return 0;
    case QaLabelScope.ASSAY:
      return 1;
    case QaLabelScope.SAMPLE:
      return 2;
    case QaLabelScope.TRIPLICATE:
      return 3;
    case QaLabelScope.TECHREP:
      return 4;
  }
}

export function getQaTechRepFromSampleKey(sample: string): 1 | 2 | 3 | null {
  const match = sample.match(/-rep([1-3])$/i);
  return match ? (parseInt(match[1], 10) as 1 | 2 | 3) : null;
}

export function buildQaLabelScopeEntryKey(scope: QaLabelScope, entry: QaLabelScopeEntry): string {
  return `${scope}|${entry.sampleId ?? 'null'}|${entry.assay ?? 'null'}|${entry.techRep ?? 'null'}`;
}

export function qaLabelToScopeEntry(label: Pick<QaLabel, 'sampleId' | 'assay' | 'techRep'>): QaLabelScopeEntry {
  return {
    sampleId: label.sampleId ?? null,
    assay: label.assay ?? null,
    techRep: label.techRep ?? null,
  };
}

export function doesQaLabelAffectConcreteRep(
  label: Pick<QaLabel, 'scope' | 'sampleId' | 'assay' | 'techRep'>,
  rep: QaConcreteRepRef,
): boolean {
  switch (label.scope) {
    case QaLabelScope.CHIP:
      return true;
    case QaLabelScope.TECHREP:
      return label.sampleId === rep.sampleId && label.assay === rep.assay && label.techRep === rep.techRep;
    case QaLabelScope.TRIPLICATE:
      return label.sampleId === rep.sampleId && label.assay === rep.assay;
    case QaLabelScope.SAMPLE:
      return label.sampleId === rep.sampleId;
    case QaLabelScope.ASSAY:
      return label.assay === rep.assay;
  }
}

export function getQaResultLabelKind(labelType: QaLabelType | null | undefined): QaResultLabelKind {
  switch (labelType) {
    case QaLabelType.PRODUCT:
      return 'PRODUCT';
    case QaLabelType.NO_PRODUCT:
    case QaLabelType.PRIMER_DIMER:
    case QaLabelType.UNSPECIFIC:
      return 'NON_PRODUCT';
    case QaLabelType.NONSENSE:
    case QaLabelType.OTHER:
      return 'UNCLEAR';
    case QaLabelType.ISSUE:
      return 'ISSUE';
    default:
      return 'NONE';
  }
}

export function isQaResultLabelDisqualifying(labelType: QaLabelType | null | undefined): boolean {
  return labelType != null && labelType !== QaLabelType.PRODUCT;
}

function compareQaLabelsByPrecedence(a: QaLabel, b: QaLabel): number {
  const appliesDelta = Number(b.appliesToResults) - Number(a.appliesToResults);
  if (appliesDelta !== 0) {
    return appliesDelta;
  }

  const specificityDelta = getQaLabelScopeSpecificity(b.scope) - getQaLabelScopeSpecificity(a.scope);
  if (specificityDelta !== 0) {
    return specificityDelta;
  }

  const createdAtDelta = b.createdAt.getTime() - a.createdAt.getTime();
  if (createdAtDelta !== 0) {
    return createdAtDelta;
  }

  return b.id - a.id;
}

export function resolveQaLabelsForConcreteRep(
  labels: QaLabel[],
  rep: QaConcreteRepRef,
): { effectiveResultLabel: QaLabel | null; labels: QaResolvedLabelSummary[] } {
  const matching = labels.filter(label => doesQaLabelAffectConcreteRep(label, rep)).sort(compareQaLabelsByPrecedence);
  const effectiveResultLabel =
    matching.find(label => label.appliesToResults && doesQaLabelTypeAllowResultApplication(label.labelType)) ?? null;

  return {
    effectiveResultLabel,
    labels: matching.map(label => ({
      ...label,
      effectiveForResults: effectiveResultLabel?.id === label.id,
      overriddenByLabelId:
        label.appliesToResults && effectiveResultLabel != null && effectiveResultLabel.id !== label.id
          ? effectiveResultLabel.id
          : null,
    })),
  };
}
